import { SalesDashboardLead, SalesDashboardNote } from '.';
import * as classNames from 'classnames';
import {
  CreateLeadModalStatus,
  EDashboardView,
} from 'common/interfaces/provider';
import { StateModalStatus } from 'common/interfaces/state';
import { UserType } from 'common/interfaces/user';
import { Search } from 'components/action';
import { ActivitiesGroup } from 'components/activity';
import { SectionHeadline } from 'components/content';
import { CreateLeadModal } from 'components/createlead';
import { LoadingFullScreen } from 'components/indicator';
import { Layout } from 'components/layout';
import { Pagination } from 'components/navigation/Pagination';
import { PendingPricingRequestsList } from 'components/pricing';
import { State, StateAreaListPicker } from 'components/state';
import { activityDatesDropdownOptions } from 'data/sales';
import { action, computed, toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { IApp } from 'stores/AllStores';
import { LeadsSearchFilter } from 'stores/Leads';
import * as zenscroll from 'zenscroll';
import { PricingRequestModal } from './../Lead/PricingRequest';
import { SvgGrid, SvgList } from 'components/svg';

// Styles
const s = require('./SalesDashboard.css');
const addIcon = require('assets/plus icon.svg');
const arrowToRightIcon = require('assets/blue arrow.svg');

// Page
@inject('store')
@observer
export class SalesDashboard extends React.Component<IApp, {}> {
  @computed private get activityTypesDropdownOptions(): Array<{
    key: string;
    value: any;
    label: string;
  }> {
    return [
      {
        key: 'all-activities-option',
        value: 0,
        label: 'All',
      },
    ].concat(
      toJS(
        this.props.store.activities.allActivityTypes.map((activityType, i) => {
          return {
            key:
              typeof activityType.value === 'string'
                ? activityType.value + i.toString()
                : JSON.stringify(activityType.value) + i,
            value: activityType.value,
            label: activityType.label,
          };
        })
      )
    );
  }

  private get activityDatesDropdownOptions(): Array<{
    key: string;
    value: string;
    label: string;
  }> {
    return activityDatesDropdownOptions();
  }

  private offsetScroll = zenscroll.createScroller(
    (document.scrollingElement || document.body) as any,
    0,
    140
  );

  public componentDidMount() {
    const {
      store: {
        lead: {
          generateDashboardLeadCardId,
          generateDashboardLeadCardNameId,
          generateDashboardNoteCardId,
          generateDashboardNoteCardNameId,
        },
        leads: { lastLeadId, lastNoteId, leadsDashboardView },
      },
    } = this.props;

    // Scroll to last opened lead
    const leadCardId =
      leadsDashboardView === EDashboardView.List
        ? generateDashboardLeadCardNameId(lastLeadId)
        : generateDashboardLeadCardId(lastLeadId);

    const lastLeadCardElem = document.getElementById(leadCardId);
    if (lastLeadCardElem) {
      this.offsetScroll.to(lastLeadCardElem, 0);
    } else {
      // Or scroll to last opened note
      const noteCardNameId =
        leadsDashboardView === EDashboardView.List
          ? generateDashboardNoteCardNameId(lastNoteId)
          : generateDashboardNoteCardId(lastNoteId);
      const lastNoteCardElem = document.getElementById(noteCardNameId);

      if (lastNoteCardElem) {
        this.offsetScroll.to(lastNoteCardElem, 0);
      } else {
        zenscroll.toY(0);
      }
    }

    // Fetch provider types and dispenser classes
    this.props.store.leads.fetchProviderTypes();
    this.props.store.leads.fetchDispenserClasses();
  }

  public render() {
    const { user, leads, activities, pricingRequest } = this.props.store;

    return (
      <Layout>
        <div className={s.salesDashboard}>
          <div className={s.leads}>
            <div className={s.leadsList}>
              <div className={s.fixedHeader}>
                <div className={s.leadsSearch}>
                  <Search
                    id="leads-search-field"
                    onValueChange={this.handleSearchChange}
                    onOptionChange={this.handleSearchFilterChange}
                    classNameForField={s.searchField}
                    autoFocus={true}
                    label="Search leads"
                    placeholder={
                      leads.leadsSearchFilter === LeadsSearchFilter.All
                        ? 'Search leads by name, address or phone number...'
                        : leads.leadsSearchFilter === LeadsSearchFilter.Phone
                        ? 'Search leads by phone number...'
                        : leads.leadsSearchFilter === LeadsSearchFilter.Name
                        ? 'Search leads by name...'
                        : leads.leadsSearchFilter === LeadsSearchFilter.Notes
                        ? 'Search leads by notes content...'
                        : 'Search leads by address...'
                    }
                    isValueClearable={true}
                    clearOnlySearchValue={
                      leads.leadsSearchFilter === LeadsSearchFilter.Notes
                        ? true
                        : false
                    }
                    value={
                      leads.leadsSearchFilter === LeadsSearchFilter.Notes
                        ? leads.notesSearchQuery
                        : leads.leadsSearch
                    }
                    selectedOption={leads.leadsSearchFilter}
                    options={Object.keys(LeadsSearchFilter).map(
                      (searchFilterLabel) => ({
                        key: searchFilterLabel,
                        value: LeadsSearchFilter[searchFilterLabel],
                        label: searchFilterLabel,
                      })
                    )}
                  />

                  {leads.leadsSearchFilter !== LeadsSearchFilter.Notes && (
                    <Search
                      id="leads-last-activity-type-filter"
                      onOptionChange={
                        this.handleSearchFilterByActivityTypeChange
                      }
                      className={s.lastLeadActivityType}
                      label="Last Lead's activity type"
                      selectedOption={leads.leadsFilterByActivityTypeId}
                      options={this.activityTypesDropdownOptions.map(
                        (activity) => ({
                          key: activity.key,
                          value: activity.value,
                          label: activity.label,
                        })
                      )}
                    />
                  )}

                  {leads.leadsSearchFilter !== LeadsSearchFilter.Notes && (
                    <Search
                      id="leads-last-activity-date-filter"
                      onOptionChange={
                        this.handleSearchFilterByActivityDateChange
                      }
                      className={s.lastLeadActivityDate}
                      label="Last lead's activity date"
                      selectedOption={
                        leads.leadsFilterByActivityDate !== null &&
                        leads.leadsFilterByActivityDate.from !== null &&
                        leads.leadsFilterByActivityDate.to !== null
                          ? leads.leadsFilterByActivityDate.from +
                            '/' +
                            leads.leadsFilterByActivityDate.to
                          : leads.leadsFilterByActivityDate !== null &&
                            leads.leadsFilterByActivityDate.noActivity === true
                          ? -1
                          : 0
                      }
                      options={this.activityDatesDropdownOptions.map(
                        (date) => ({
                          key: date.key,
                          value: date.value,
                          label: date.label,
                        })
                      )}
                    />
                  )}

                  <div className={s.viewToggle}>
                    <button
                      onClick={this.handleTogglingDashboardView(
                        EDashboardView.Grid
                      )}
                      className={classNames(s.viewToggleButton, {
                        [s.active]:
                          leads.leadsDashboardView === EDashboardView.Grid,
                      })}
                    >
                      <SvgGrid />
                    </button>
                    <button
                      onClick={this.handleTogglingDashboardView(
                        EDashboardView.List
                      )}
                      className={classNames(s.viewToggleButton, {
                        [s.active]:
                          leads.leadsDashboardView === EDashboardView.List,
                      })}
                    >
                      <SvgList />
                    </button>
                  </div>
                </div>
              </div>

              {(leads.leadsSearchFilter !== LeadsSearchFilter.Notes && (
                <div
                  className={classNames(s.leadsListResults, {
                    [s.listIsEmpty]:
                      (leads.leadsToDisplay.firstFetch &&
                        leads.leadsToDisplay.isLoading) ||
                      (!leads.leadsToDisplay.firstFetch &&
                        !leads.leadsToDisplay.isLoading &&
                        leads.leadsToDisplay.totalLeads <= 0),
                  })}
                >
                  {(leads.leadsToDisplay.firstFetch &&
                    leads.leadsToDisplay.isLoading && (
                      <div className={s.listNoResults}>
                        <p>Loading leads...</p>
                      </div>
                    )) ||
                    (leads.leadsToDisplay.firstFetch === false &&
                      leads.leadsToDisplay.isLoading === false &&
                      leads.leadsToDisplay.totalLeads <= 0 && (
                        <div className={s.listNoResults}>
                          <p>No leads have been found</p>
                        </div>
                      )) || (
                      <div className={s.listWithLeads}>
                        <div
                          className={classNames({
                            [s.leadsGrid]:
                              leads.leadsDashboardView === EDashboardView.Grid,
                            [s.leadsList]:
                              leads.leadsDashboardView === EDashboardView.List,
                            [s.single]: leads.leadsToDisplayDetails.length <= 1,
                            [s.double]:
                              leads.leadsToDisplayDetails.length === 2,
                          })}
                        >
                          {leads.leadsToDisplayDetails.map(
                            (lead, leadIndex) => (
                              <SalesDashboardLead
                                key={lead.databaseId}
                                index={leadIndex}
                                lead={lead}
                              />
                            )
                          )}
                        </div>
                      </div>
                    )}

                  {leads.leadsToDisplay.totalLeads > 0 && (
                    <Pagination
                      onPageClick={this.handleLeadsPageChange}
                      isLoading={leads.leadsToDisplay.isLoading}
                      totalEntries={leads.leadsToDisplay.totalLeads}
                      entriesPerPage={leads.LEADS_OR_NOTES_PER_PAGE_LIMIT}
                      showingFromOffset={leads.leadsFetchOffset}
                    />
                  )}
                </div>
              )) || (
                <div
                  className={classNames(s.leadsListResults, {
                    [s.listIsEmpty]:
                      (leads.notesToDisplay.firstFetch &&
                        leads.notesToDisplay.isLoading) ||
                      (!leads.notesToDisplay.firstFetch &&
                        !leads.notesToDisplay.isLoading &&
                        leads.notesToDisplay.totalNotes <= 0),
                  })}
                >
                  {(leads.notesToDisplay.firstFetch &&
                    leads.notesToDisplay.isLoading && (
                      <div className={s.listNoResults}>
                        <p>Loading notes...</p>
                      </div>
                    )) ||
                    (leads.notesToDisplay.firstFetch === false &&
                      leads.notesToDisplay.isLoading === false &&
                      leads.notesToDisplay.totalNotes <= 0 && (
                        <div className={s.listNoResults}>
                          <p>No notes have been found</p>
                        </div>
                      )) || (
                      <div className={s.listWithLeads}>
                        <div
                          className={classNames({
                            [s.leadsGrid]:
                              leads.leadsDashboardView === EDashboardView.Grid,
                            [s.leadsList]:
                              leads.leadsDashboardView === EDashboardView.List,
                            [s.single]: leads.notesToDisplayDetails.length <= 1,
                            [s.double]:
                              leads.notesToDisplayDetails.length === 2,
                          })}
                        >
                          {leads.notesToDisplayDetails.map(
                            (note, noteIndex) => (
                              <SalesDashboardNote
                                key={note.id}
                                index={noteIndex}
                                note={note}
                              />
                            )
                          )}
                        </div>
                      </div>
                    )}

                  {leads.notesToDisplay.totalNotes > 0 && (
                    <Pagination
                      onPageClick={this.handleNotesPageChange}
                      isLoading={leads.notesToDisplay.isLoading}
                      totalEntries={leads.notesToDisplay.totalNotes}
                      entriesPerPage={leads.LEADS_OR_NOTES_PER_PAGE_LIMIT}
                      showingFromOffset={leads.notesFetchOffset}
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          <div className={s.leadsActivities}>
            <SectionHeadline
              className={s.assignRepsHeadline}
              title="Create new lead"
              button={{
                label: 'Create',
                icon: addIcon,
                iconClassName: s.createNewLeadIcon,
                onClick: this.handleOpenCreateNewLeadModal,
              }}
            />

            {user.userType !== null && user.userType === UserType.SalesManager && (
              <SectionHeadline
                className={s.assignRepsHeadline}
                title="Sales reps areas"
                button={{
                  label: 'View and assign',
                  icon: arrowToRightIcon,
                  iconClassName: s.assignRepsIcon,
                  onClick: this.handleOpenRepsAreasModal,
                }}
              />
            )}

            <SectionHeadline title="Pricing requests" />

            {(pricingRequest.pricingRequestsLength > 0 && (
              <PendingPricingRequestsList />
            )) || (
              <p className={s.empty}>
                All pending pricing requests have been approved.
              </p>
            )}

            <SectionHeadline title="Activities" />

            {(activities.activitiesUserLength > 0 && (
              <div>{this.renderActivitiesListByGroup()}</div>
            )) || (
              <p className={s.empty}>
                You have finished all of your activities.
              </p>
            )}

            <LoadingFullScreen
              className={s.spinner}
              fitInContainer={true}
              active={
                (activities.userActivitiesLoading &&
                  activities.activitiesUserLength <= 0) ||
                (pricingRequest.loadingPricingRequests &&
                  pricingRequest.pricingRequestsLength <= 0)
              }
            />
          </div>
        </div>

        <PricingRequestModal
          show={
            pricingRequest.loadingSinglePricingRequest ||
            pricingRequest.singlePricingRequest !== null
          }
          existingRequest={{
            data: pricingRequest.singlePricingRequest,
            status: pricingRequest.statusOfSinglePricingRequestApproval,
            loading: pricingRequest.loadingSinglePricingRequest,
          }}
          onModalClose={this.handlePricingRequestClose}
        />

        <State />
        <StateAreaListPicker />

        <CreateLeadModal />
      </Layout>
    );
  }

  private renderActivitiesListByGroup() {
    return this.props.store.activities.userActivitiesGroups.map(
      (group, key) => {
        return group.activities.length > 0 ? (
          <ActivitiesGroup
            key={group.key}
            className="dashboardActivities"
            title={group.title}
            allFinished={group.allFinished}
            activities={group.activities}
          />
        ) : null;
      }
    );
  }

  private scrollToTop = () => {
    zenscroll.toY(0);
  };

  private handleSearchChange = (value: string) => {
    const { leads } = this.props.store;

    this.scrollToTop();

    if (leads.leadsSearchFilter === LeadsSearchFilter.Notes) {
      leads.notesSearchQuery = value;
    } else {
      leads.leadsSearch = value;
    }
  };

  private handleSearchFilterChange = (value: string) => {
    const { leads } = this.props.store;

    this.scrollToTop();

    const currentFilterValue = leads.leadsSearchFilter.slice() as LeadsSearchFilter;
    const nextFilterValue = value as LeadsSearchFilter;

    // Copy value
    if (currentFilterValue !== nextFilterValue) {
      if (currentFilterValue === LeadsSearchFilter.Notes) {
        leads.leadsSearch = leads.notesSearchQuery;
      } else if (nextFilterValue === LeadsSearchFilter.Notes) {
        leads.notesSearchQuery = leads.leadsSearch;
      }
    }

    leads.leadsSearchFilter = nextFilterValue
      ? nextFilterValue
      : LeadsSearchFilter.All;
  };

  private handleSearchFilterByActivityTypeChange = (value: number) => {
    this.scrollToTop();
    this.props.store.leads.leadsFilterByActivityTypeId = value;
  };

  private handleSearchFilterByActivityDateChange = (value: string | number) => {
    this.scrollToTop();

    if (value === 0) {
      this.props.store.leads.leadsFilterByActivityDate = {
        from: null,
        to: null,
        noActivity: false,
      };
    } else if (value === -1) {
      this.props.store.leads.leadsFilterByActivityDate = {
        from: null,
        to: null,
        noActivity: true,
      };
    } else if (typeof value === 'string') {
      const activityDateExploded = value.split('/');
      if (activityDateExploded.length >= 2) {
        this.props.store.leads.leadsFilterByActivityDate = {
          from: activityDateExploded[0],
          to: activityDateExploded[1],
          noActivity: false,
        };
      }
    }
  };

  @action
  private handleOpenRepsAreasModal = () => {
    this.props.store.state.modalStatus = StateModalStatus.OpenOnRepAreasList;
  };

  @action
  private handleOpenCreateNewLeadModal = () => {
    this.props.store.leads.modalStatus = CreateLeadModalStatus.Opened;
  };

  @action
  private handlePricingRequestClose = () => {
    this.props.store.leadPricing.fetchAllItemsWithPricing();
    this.props.store.pricingRequest.resetSinglePricingRequest();
  };

  @action
  private handleLeadsPageChange = (page: number) => {
    this.props.store.leads.changeLeadsPage(page);
    this.scrollToTop();
  };

  @action
  private handleNotesPageChange = (page: number) => {
    this.props.store.leads.changeNotesPage(page);
    this.scrollToTop();
  };

  @action
  private handleTogglingDashboardView = (view: EDashboardView) => (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    if (view !== this.props.store.leads.leadsDashboardView) {
      this.props.store.leads.setLeadsDashboardView(view);
      this.scrollToTop();
    }
  };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/routes/sales/Dashboard/SalesDashboard.tsx